import { getCart } from '../api/shared-app-requests';
import { productsCoveredByDiscount } from '../api/candyrack';
import { REGISTERED_APPS } from '../types/Apps';
import { logError, logInfo } from './logging';
import storage from '../product-page-placement/storage';
import {
  CANDYRACK_CHECKOUT_BUTTON_DISCOUNTED_PRODUCT_LOCAL_STORAGE_KEY,
  CANDYRACK_DISCOUNT_CODES_LOCAL_STORAGE_KEY,
  CANDYRACK_DISCOUNTED_PRODUCTS_LOCAL_STORAGE_KEY,
} from '../constants/app';
import {
  loadCartAndCleanupAssociatedProductData,
  showInfoInCart,
} from './cart';
import throttle from './throttle';
import applyDiscounts from '../../graphql/utils/applyDiscounts';

const MAXIMALY_ALLOWED_DISCOUNTS_BY_SHOPIFY = 5;

// used in CR popup and ProductPagePlacement
export async function saveDiscountCodeForCandyrack(
  code: string,
  variantId: number,
  discountedPrice: number | null,
  discount: string,
  relatedProductId: number,
  hasParent: boolean,
  storefrontAccessToken: string | undefined,
) {
  logInfo(
    `Saving discount code, discounted price ${discountedPrice}, discount: ${discount}`,
    REGISTERED_APPS.CANDYRACK,
  );

  await applyDiscounts({
    token: storefrontAccessToken,
    discountCodes: [code],
  });

  const currentDiscounts =
    storage.getItem(CANDYRACK_DISCOUNT_CODES_LOCAL_STORAGE_KEY) || '';

  const discountsCount = currentDiscounts.split(',').length;
  // Adding only unique discounts
  if (
    !currentDiscounts.includes(code) ||
    discountsCount > MAXIMALY_ALLOWED_DISCOUNTS_BY_SHOPIFY
  ) {
    const discountCodes =
      currentDiscounts.trim().length === 0
        ? code
        : `${currentDiscounts},${code}`;
    storage.setItem(CANDYRACK_DISCOUNT_CODES_LOCAL_STORAGE_KEY, discountCodes);
    updateDiscounts(REGISTERED_APPS.CANDYRACK);
  }

  const currentDiscountedProducts = getDiscountedProductWithDiscount();
  storage.setItem(
    CANDYRACK_DISCOUNTED_PRODUCTS_LOCAL_STORAGE_KEY,
    JSON.stringify([
      ...currentDiscountedProducts,
      {
        variantId,
        discountedPrice,
        code,
        discount,
        relatedProductId,
        hasParent,
      },
    ]),
  );
}

// Currently used in the checkout button
export async function saveDiscountForCandyRackCheckoutButton(
  discount: string,
  variantId?: number,
  storefrontAccessToken?: string,
) {
  await applyDiscounts({
    token: storefrontAccessToken,
    discountCodes: [discount],
  });
  const currentDiscounts =
    storage.getItem(CANDYRACK_DISCOUNT_CODES_LOCAL_STORAGE_KEY) || '';
  logInfo(
    `Current discounts in storage: ${currentDiscounts}`,
    REGISTERED_APPS.CANDYCART,
  );
  const discountsCount = currentDiscounts.split(',').length;
  if (discountsCount > MAXIMALY_ALLOWED_DISCOUNTS_BY_SHOPIFY) {
    logInfo(
      `Too many discounts: ${discountsCount}. Returning.`,
      REGISTERED_APPS.CANDYCART,
    );
    return;
  }
  // Adding only unique discounts
  if (!currentDiscounts.includes(discount)) {
    const discountCodes =
      currentDiscounts.trim().length === 0
        ? discount
        : `${currentDiscounts},${discount}`;
    logInfo(
      `Discounts saved in storage after addition: ${discountCodes}`,
      REGISTERED_APPS.CANDYCART,
    );
    storage.setItem(CANDYRACK_DISCOUNT_CODES_LOCAL_STORAGE_KEY, discountCodes);
  }
  const currentProducts = JSON.parse(
    storage.getItem(
      CANDYRACK_CHECKOUT_BUTTON_DISCOUNTED_PRODUCT_LOCAL_STORAGE_KEY,
    ) || '[]',
  );
  storage.setItem(
    CANDYRACK_CHECKOUT_BUTTON_DISCOUNTED_PRODUCT_LOCAL_STORAGE_KEY,
    JSON.stringify([
      ...currentProducts,
      {
        variantId,
        discount,
      },
    ]),
  );
}

/*
 * TODO: removeCandyRackCheckoutButtonDiscount, removeCandyRackAddToCartDiscount are amost identical.
 * Please refactor to avoid duplication next time we have something in that corner (so that some QA gets done).
 */
export function removeCandyRackCheckoutButtonDiscount(
  discount: string,
  variantId?: number,
) {
  const currentDiscounts =
    storage.getItem(CANDYRACK_DISCOUNT_CODES_LOCAL_STORAGE_KEY) || '';
  logInfo(`Current discounts: ${currentDiscounts}`, REGISTERED_APPS.CANDYCART);

  const discountedProducts = JSON.parse(
    storage.getItem(
      CANDYRACK_CHECKOUT_BUTTON_DISCOUNTED_PRODUCT_LOCAL_STORAGE_KEY,
    ) || '[]',
  );

  const updatedProducts = discountedProducts.filter(
    (product: any) => product.variantId !== variantId,
  );

  const isDiscountNotUsedByOtherProducts = !updatedProducts.find(
    (product: any) => product.code === discount,
  );
  if (isDiscountNotUsedByOtherProducts) {
    const updatedDiscounts = currentDiscounts
      .split(',')
      .filter((singleDiscount) => singleDiscount !== discount)
      .join(',');
    storage.setItem(
      CANDYRACK_DISCOUNT_CODES_LOCAL_STORAGE_KEY,
      updatedDiscounts,
    );
    logInfo(
      `Discount ${discount} not used by other products, removed. Updated discounts: ${updatedDiscounts}`,
      REGISTERED_APPS.CANDYCART,
    );
  }
  storage.setItem(
    CANDYRACK_CHECKOUT_BUTTON_DISCOUNTED_PRODUCT_LOCAL_STORAGE_KEY,
    JSON.stringify(updatedProducts),
  );
}

/*
 * TODO: removeCandyRackCheckoutButtonDiscount, removeCandyRackAddToCartDiscount are amost identical.
 * Please refactor to avoid duplication next time we have something in that corner (so that some QA gets done).
 */
export function removeCandyRackAddToCartDiscount(
  discount: string,
  variantId?: number,
) {
  const currentDiscounts =
    storage.getItem(CANDYRACK_DISCOUNT_CODES_LOCAL_STORAGE_KEY) || '';
  logInfo(`Current discounts: ${currentDiscounts}`, REGISTERED_APPS.CANDYRACK);

  const currentDiscountedProducts = getDiscountedProductWithDiscount();

  const updatedProducts = currentDiscountedProducts.filter(
    (product: any) => product.variantId !== variantId,
  );

  const isDiscountNotUsedByOtherProducts = !updatedProducts.find(
    (product: any) => product.code === discount,
  );
  if (isDiscountNotUsedByOtherProducts) {
    const updatedDiscounts = currentDiscounts
      .split(',')
      .filter((singleDiscount) => singleDiscount !== discount)
      .join(',');
    storage.setItem(
      CANDYRACK_DISCOUNT_CODES_LOCAL_STORAGE_KEY,
      updatedDiscounts,
    );
    logInfo(
      `Discount ${discount} not used by other products, removed. Updated discounts: ${updatedDiscounts}`,
      REGISTERED_APPS.CANDYRACK,
    );
  }
  storage.setItem(
    CANDYRACK_DISCOUNTED_PRODUCTS_LOCAL_STORAGE_KEY,
    JSON.stringify(updatedProducts),
  );
}

export function updateDiscounts(runningAppName: REGISTERED_APPS) {
  applyDiscount(runningAppName);
}

export function getDiscountCodesString(
  runningAppName: REGISTERED_APPS,
): string {
  let code: string | null = null;
  if (REGISTERED_APPS.CANDYRACK === runningAppName) {
    code = storage.getItem(CANDYRACK_DISCOUNT_CODES_LOCAL_STORAGE_KEY);
  }
  return code || '';
}

export function isMultipleDiscountsExist(
  runningAppName: REGISTERED_APPS,
): boolean {
  const candyrackPrePurchaseCodesString =
    getDiscountCodesString(runningAppName).trim();
  return candyrackPrePurchaseCodesString.split(',').length > 1;
}

/*
 * Returns the discount codes as an escaped string, to avoid broken
 * URLs by the percentage sign
 * Different discounts are escaped by comma.
 * Hint: note that encodeURIComponent(null) will return 'null', and similar
 * behaviour for undefined.
 */
export function getDiscountCodesStringSeparatedByCommaAndEscaped(
  runningAppName: REGISTERED_APPS,
): string {
  const candyrackPrePurchaseCodesString =
    getDiscountCodesString(runningAppName)?.trim();

  if (!candyrackPrePurchaseCodesString) {
    return '';
  }
  const discountCodesArray = candyrackPrePurchaseCodesString.split(',');
  return (
    discountCodesArray
      // We are choosing encodeURIComponent over encodeURI, as we want to encode only part of the URL.
      .map((discount) => encodeURIComponent(discount))
      .join(',')
  );
}

function applyDiscount(runningAppName: REGISTERED_APPS) {
  const code = getDiscountCodesString(runningAppName);

  if (REGISTERED_APPS.CANDYRACK === runningAppName) {
    attachDiscountInfoForCandyrack(code);
  }
}

async function attachDiscountInfoForCandyrack(codes: string) {
  logInfo('Showing discount info in cart', REGISTERED_APPS.CANDYRACK);

  if (!codes.trim()) {
    logInfo('No discount, returning.', REGISTERED_APPS.CANDYRACK);
    return;
  }

  if (!document.querySelector("a[href*='?variant=']")) {
    logInfo('No variant, returning.', REGISTERED_APPS.CANDYRACK);
    return;
  }

  const cart = await getCart();
  if (cart.items.length === 0) {
    return;
  }
  const productIds = cart.items.map((item: any) => item.product_id);
  const variantIds = cart.items.map((item: any) => item.variant_id);
  const codesArray = codes.split(',');
  const variantsEligibleForDiscount = await productsCoveredByDiscount(
    codesArray,
    productIds,
    variantIds,
  );
  showInfoInCart(variantsEligibleForDiscount);
}

function watchForDrawerCart(runningAppName: REGISTERED_APPS) {
  const throttledCleanupAssociatedProductData = throttle(
    () => loadCartAndCleanupAssociatedProductData(runningAppName),
    3000,
  );
  const callback = function (mutationsList: any) {
    throttledCleanupAssociatedProductData();
  };

  try {
    const observer = new MutationObserver(callback);
    const targetNode = document.body;
    observer.observe(targetNode, { childList: true, subtree: true });
  } catch (error) {
    logError('Failed to setup drawer cart observer', runningAppName, error);
  }
}

export function clearDiscount() {
  logInfo(`Clear discount codes information`, REGISTERED_APPS.CANDYRACK);
  storage.removeItem(CANDYRACK_DISCOUNT_CODES_LOCAL_STORAGE_KEY);
  storage.removeItem(CANDYRACK_DISCOUNTED_PRODUCTS_LOCAL_STORAGE_KEY);
}

export function getDiscountedProductWithDiscount() {
  const data = storage.getItem(CANDYRACK_DISCOUNTED_PRODUCTS_LOCAL_STORAGE_KEY);
  if (data) {
    try {
      return JSON.parse(data);
    } catch {
      return [];
    }
  }
  return [];
}

export function handleDiscounts(runningAppName: REGISTERED_APPS) {
  updateDiscounts(runningAppName);
  watchForDrawerCart(runningAppName);
}

export function calculateDiscountedPrice(
  variantPrice: number,
  amount: number,
  amount_type?: string,
  apply_discount?: boolean,
  app?: REGISTERED_APPS,
) {
  if (apply_discount) {
    if (amount_type === 'percentage') {
      // Percentage discount: compute and truncate
      const discountRaw = variantPrice * (amount / 100);
      const discountTruncated = Math.floor(discountRaw * 100) / 100;
      const discountedPrice = variantPrice - discountTruncated;

      logInfo(`Applying percentage discount: ${discountTruncated}`, app);
      logInfo(`Final discounted price: ${discountedPrice}`, app);
      return discountedPrice;
    } else {
      // Fixed amount discount: subtract amount as-is
      const discountedPrice = variantPrice - amount;

      logInfo(`Applying fixed discount: ${amount}`, app);
      logInfo(`Final discounted price: ${discountedPrice}`, app);
      return discountedPrice;
    }
  } else {
    logInfo(
      `Discount is not set to be applied, returning original variantPrice : ${variantPrice}`,
      app,
    );
    return variantPrice;
  }
}

export async function cleanupInvalidDiscountData(
  cart: any,
  runningAppName: REGISTERED_APPS,
) {
  const discountedProducts = getDiscountedProductWithDiscount();

  discountedProducts.forEach((discountedProduct: any) => {
    const discountedProductInCart = cart.items.find(
      (item: any) =>
        Number(item.variant_id) === Number(discountedProduct.variantId),
    );
    if (!discountedProductInCart) {
      removeCandyRackAddToCartDiscount(
        discountedProduct.code,
        Number(discountedProduct.variantId),
      );
    }
    const relatedProductInCart = cart.items.find(
      (item: any) =>
        Number(item.product_id) ===
          Number(discountedProduct.relatedProductId) ||
        !discountedProduct.hasParent,
    );
    if (!relatedProductInCart) {
      removeCandyRackAddToCartDiscount(
        discountedProduct.code,
        Number(discountedProduct.variantId),
      );
      logInfo(
        `Related product ${discountedProduct.variantId} not found. Removing discount info...`,
        runningAppName,
      );
      applyDiscount(runningAppName);
    }
  });
}
